// Doc-Ref: docs/features/collaborations-current.md
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  Building2,
  Network,
  User,
  Search,
  X,
  Loader2,
  Link as LinkIcon,
  ChevronDown,
  Star,
} from "lucide-react";
import { getAuthorProfileSummaryByID, getSuggestSearchAuthors } from "Explore/Services/Author";
import { getInstitutionDetailSummaryByID, getSuggestSearchInstitutions } from "Explore/Services/Institution";
import { getUnitSummaryById, getSuggestSearchUnits } from "Explore/Services/Unit";
import useRecentEntities from "./useRecentEntities";
import useFavoriteEntities from "./useFavoriteEntities";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Skeleton } from "components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { cn } from "lib/utils";

const URL_STATUS = { idle: "idle", resolving: "resolving", resolved: "resolved", error: "error" };

const EntitySelector = ({
  selectedEntity,
  onEntitySelect,
  onEntityClear,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const [urlStatus, setUrlStatus] = useState(URL_STATUS.idle);
  const [urlMessage, setUrlMessage] = useState("");

  const [searchResults, setSearchResults] = useState({
    institutions: [],
    units: [],
    researchers: []
  });

  const searchContainerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const { recentEntities, addRecentEntity } = useRecentEntities();
  const { favorites, isLoading: isFavoritesLoading, hasFavorites, isLoggedIn } = useFavoriteEntities();
  const [expandedGroups, setExpandedGroups] = useState({});

  // Handle click outside to close dropdown and reset collapsed state
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setExpandedGroups({});
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const normalizeEntity = useCallback((raw, type) => {
    return {
      ...raw,
      id: raw.id,
      name: raw.name,
      type,
    };
  }, []);

  const handleEntitySelect = useCallback((entity) => {
    setIsDropdownOpen(false);
    setExpandedGroups({});
    setSearchValue("");
    setUrlStatus(URL_STATUS.idle);
    setUrlMessage("");

    const handleApiResult = (data, resolvedType) => {
      const canonicalId = data?.id || entity.id;
      const isNum = canonicalId && !isNaN(Number(canonicalId));
      const sealUrl = isNum && (resolvedType === "institution" || !resolvedType)
        ? `https://media.academic.online/institutions_images/${canonicalId}/seal.png`
        : undefined;
      const fullEntity = {
        ...entity,
        ...data,
        id: canonicalId,
        type: resolvedType,
        photo: data?.photo || data?.logo_url || entity.photo || sealUrl,
        avatar: data?.photo || data?.avatar || entity.avatar || sealUrl,
      };
      onEntitySelect(fullEntity);
      addRecentEntity(fullEntity);
    };

    if (entity.type === "researcher") {
      getAuthorProfileSummaryByID(entity.id, (data) => handleApiResult(data, "researcher"));
    } else if (entity.type === "institution") {
      getInstitutionDetailSummaryByID(entity.id, (data) => handleApiResult(data, "institution"));
    } else if (entity.type === "unit") {
      getUnitSummaryById(entity.id).then((data) => handleApiResult(data, "unit"));
    } else {
      onEntitySelect(entity);
      addRecentEntity(entity);
    }
  }, [onEntitySelect, addRecentEntity]);

  const handleUrlResolve = useCallback((url) => {
    if (!url.trim()) return;

    let entityId = null;
    let type = null;

    try {
      const parsedUrl = new URL(url);
      const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

      if (pathParts.length >= 2) {
        entityId = pathParts[pathParts.length - 1];
        const typeStr = pathParts[pathParts.length - 2];

        if (typeStr === "faculty") type = "researcher";
        else if (typeStr === "institution") type = "institution";
        else if (typeStr === "unit") type = "unit";
      }
    } catch (e) {
      // Ignore if not a valid URL
    }

    setUrlStatus(URL_STATUS.resolving);
    setUrlMessage("Resolving URL...");

    if (entityId && type) {
      const handleApiResult = (data, resolvedType) => {
        const canonicalId = data?.id || entityId;
        const isNum = canonicalId && !isNaN(Number(canonicalId));
        const sealUrl = isNum && resolvedType === "institution"
          ? `https://media.academic.online/institutions_images/${canonicalId}/seal.png`
          : undefined;

        if (data && (data.name || data.inst_name || data.display_name)) {
          setUrlStatus(URL_STATUS.resolved);
          setUrlMessage("URL resolved successfully.");
          const fullEntity = {
            ...data,
            id: canonicalId,
            type: resolvedType,
            name: data.name || data.inst_name || data.display_name,
            photo: data.photo || data.logo_url || data.photo_url || sealUrl,
            avatar: data.photo || data.avatar || data.logo_url || data.photo_url || sealUrl,
            ...(resolvedType === "unit" && {
              institution_id: data.institution_id,
              institution_name: data.institution_name,
              level: data.level,
            }),
          };
          onEntitySelect(fullEntity);
          addRecentEntity(fullEntity);
          setIsDropdownOpen(false);
          setSearchValue("");
        } else {
          setUrlStatus(URL_STATUS.error);
          setUrlMessage("Could not resolve this URL. Entity not found.");
        }
      };

      if (type === "researcher") {
        getAuthorProfileSummaryByID(entityId, (data) => handleApiResult(data, "researcher"));
      } else if (type === "institution") {
        getInstitutionDetailSummaryByID(entityId, (data) => handleApiResult(data, "institution"));
      } else if (type === "unit") {
        getUnitSummaryById(entityId).then((data) => handleApiResult(data, "unit"));
      }
      return;
    }

    setUrlStatus(URL_STATUS.error);
    setUrlMessage("Invalid profile URL. Please check and try again.");
  }, [onEntitySelect, addRecentEntity]);

  const executeSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ institutions: [], units: [], researchers: [] });
      setIsSearching(false);
      return;
    }

    // Check if it's a URL
    if (query.startsWith("http://") || query.startsWith("https://")) {
      handleUrlResolve(query);
      setIsSearching(false);
      return;
    }

    setUrlStatus(URL_STATUS.idle);
    setUrlMessage("");
    setIsSearching(true);

    try {
      const [instRes, unitRes, researcherRes] = await Promise.allSettled([
        new Promise((resolve) => getSuggestSearchInstitutions(query, resolve)),
        new Promise((resolve) => getSuggestSearchUnits(query, resolve)),
        new Promise((resolve) => getSuggestSearchAuthors(query, resolve)),
      ]);

      const institutions = instRes.status === "fulfilled" && Array.isArray(instRes.value)
        ? instRes.value.map(r => normalizeEntity(r, "institution"))
        : [];

      const units = unitRes.status === "fulfilled" && Array.isArray(unitRes.value)
        ? unitRes.value.map(r => normalizeEntity(r, "unit"))
        : [];

      const researchers = researcherRes.status === "fulfilled" && Array.isArray(researcherRes.value)
        ? researcherRes.value.map(r => normalizeEntity(r, "researcher"))
        : [];

      setSearchResults({ institutions, units, researchers });
    } catch (e) {
      console.error("Search failed", e);
    } finally {
      setIsSearching(false);
    }
  }, [normalizeEntity, handleUrlResolve]);

  const trimmedSearch = searchValue.trim();
  const searchLower = trimmedSearch.toLowerCase();
  const isFiltering = trimmedSearch.length > 0;

  const filteredFavorites = useMemo(() => {
    if (!isFiltering) {
      return favorites;
    }
    const filterList = (list) =>
      (list || []).filter((item) =>
        (item.name || "").toLowerCase().includes(searchLower)
      );
    return {
      institutions: filterList(favorites.institutions),
      units: filterList(favorites.units),
      researchers: filterList(favorites.researchers),
    };
  }, [favorites, isFiltering, searchLower]);

  const hasFilteredFavorites =
    filteredFavorites.institutions.length > 0 ||
    filteredFavorites.units.length > 0 ||
    filteredFavorites.researchers.length > 0;

  const isGroupExpanded = useCallback((groupKey) => {
    if (expandedGroups[groupKey] !== undefined) {
      return expandedGroups[groupKey];
    }
    // When filtering by query, default to expanded so matches are immediately visible
    if (isFiltering) {
      return true;
    }
    // When input is empty, default to collapsed
    return false;
  }, [expandedGroups, isFiltering]);

  const handleToggleGroup = useCallback((groupKey) => {
    setExpandedGroups((prev) => {
      const current = prev[groupKey] !== undefined ? prev[groupKey] : (isFiltering ? true : false);
      return {
        ...prev,
        [groupKey]: !current,
      };
    });
  }, [isFiltering]);

  const favoriteIds = useMemo(() => {
    const ids = new Set();
    filteredFavorites.institutions.forEach((e) => e?.id && ids.add(String(e.id)));
    filteredFavorites.units.forEach((e) => e?.id && ids.add(String(e.id)));
    filteredFavorites.researchers.forEach((e) => e?.id && ids.add(String(e.id)));
    return ids;
  }, [filteredFavorites]);

  const dedupedServerResults = useMemo(() => {
    return {
      institutions: (searchResults.institutions || []).filter(
        (e) => !favoriteIds.has(String(e.id))
      ),
      units: (searchResults.units || []).filter(
        (e) => !favoriteIds.has(String(e.id))
      ),
      researchers: (searchResults.researchers || []).filter(
        (e) => !favoriteIds.has(String(e.id))
      ),
    };
  }, [searchResults, favoriteIds]);

  const hasDedupedServerResults =
    dedupedServerResults.institutions.length > 0 ||
    dedupedServerResults.units.length > 0 ||
    dedupedServerResults.researchers.length > 0;

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    setIsDropdownOpen(true);

    if (val.length < 2) {
      setSearchResults({ institutions: [], units: [], researchers: [] });
      setUrlStatus(URL_STATUS.idle);
      setUrlMessage("");
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (val.length >= 2) {
      searchDebounceRef.current = setTimeout(() => {
        executeSearch(val);
      }, 300);
    }
  };

  const isUrlMode = searchValue.startsWith("http://") || searchValue.startsWith("https://");

  if (selectedEntity) {
    const isNumericId = selectedEntity?.id && !isNaN(Number(selectedEntity.id));
    const isNumericInstId = selectedEntity?.institution_id && !isNaN(Number(selectedEntity.institution_id));
    const instSealUrl = isNumericId && (selectedEntity?.type === "institution" || !selectedEntity?.type)
      ? `https://media.academic.online/institutions_images/${selectedEntity.id}/seal.png`
      : isNumericInstId && selectedEntity?.type === "unit"
      ? `https://media.academic.online/institutions_images/${selectedEntity.institution_id}/seal.png`
      : null;

    const logoUrl =
      selectedEntity?.photo ||
      selectedEntity?.avatar ||
      selectedEntity?.logo ||
      selectedEntity?.logo_url ||
      selectedEntity?.image ||
      instSealUrl;

    let subtitle = "";
    const inst = selectedEntity.institution_name || selectedEntity.institution;
    if (inst) {
      subtitle = typeof inst === "object" ? (inst.name || "") : inst;
    }

    const typeLabel = (selectedEntity.type || "Institution").toLowerCase();

    return (
      <div className="w-full">
        <div className="flex items-center gap-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg w-full">
          <Avatar className="h-10 w-10 shrink-0 rounded-lg bg-white border border-blue-200 shadow-sm p-0.5">
            <AvatarImage
              src={logoUrl}
              alt={selectedEntity.name}
              className="object-contain"
            />
            <AvatarFallback className="bg-explore/5 text-explore font-bold rounded-md text-xs">
              {selectedEntity.name?.substring(0, 2).toUpperCase() || "UN"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-sm font-semibold text-slate-900 truncate">
              {selectedEntity.name}
            </span>
            <span className="text-xs text-slate-500 capitalize">
              {typeLabel}{subtitle ? ` • ${subtitle}` : ""}
            </span>
          </div>
          <button
            onClick={onEntityClear}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white rounded-md transition-colors"
            title="Clear selection"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white border border-gray-300 rounded-lg p-5 gap-5 shadow-sm">

      {/* Omnisearch Input */}
      <div className="flex flex-col gap-2 relative w-full" ref={searchContainerRef}>
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Find an Entity
        </span>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center">
            {isSearching || urlStatus === URL_STATUS.resolving ? (
              <Loader2 className="text-gray-400 animate-spin" size={16} />
            ) : isUrlMode ? (
              <LinkIcon className="text-gray-400" size={16} />
            ) : (
              <Search className="text-gray-400" size={16} />
            )}
          </div>

          <Input
            placeholder="Search institutions, units, researchers or paste a profile URL..."
            className="pl-9 h-12 text-base bg-white shadow-sm border-gray-300"
            value={searchValue}
            onChange={handleSearchInputChange}
            onFocus={() => {
              setIsDropdownOpen(true);
              setExpandedGroups({});
            }}
            disabled={!!selectedEntity}
          />

          {searchValue && !selectedEntity && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              onClick={() => {
                setSearchValue("");
                setIsDropdownOpen(true);
                setExpandedGroups({});
                setUrlStatus(URL_STATUS.idle);
                setUrlMessage("");
                setSearchResults({ institutions: [], units: [], researchers: [] });
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* URL Status Message */}
        {isUrlMode && urlMessage && (
          <span className={cn(
            "text-xs mt-1",
            urlStatus === URL_STATUS.resolving ? "text-gray-500" :
              urlStatus === URL_STATUS.resolved ? "text-green-600" :
                urlStatus === URL_STATUS.error ? "text-red-600" : ""
          )}>
            {urlMessage}
          </span>
        )}

        {/* Omnisearch Dropdown */}
        {isDropdownOpen && !selectedEntity && !isUrlMode && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[400px] overflow-y-auto flex flex-col">

            {/* Case 1: Input is empty -> Show all favorites (default collapsed) */}
            {!isFiltering && (
              <div className="py-2">
                <div className="px-3.5 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-100 bg-gray-50/70">
                  <Star size={13} className="text-amber-500 fill-amber-500" />
                  <span>Favorite Entities</span>
                </div>

                {isFavoritesLoading ? (
                  <div className="p-4 flex flex-col gap-2.5">
                    <Skeleton className="h-7 w-full rounded" />
                    <Skeleton className="h-7 w-full rounded" />
                    <Skeleton className="h-7 w-full rounded" />
                  </div>
                ) : hasFavorites ? (
                  <div className="divide-y divide-gray-100">
                    {filteredFavorites.institutions.length > 0 && (
                      <CollapsibleResultGroup
                        title="Institutions"
                        icon={Building2}
                        items={filteredFavorites.institutions}
                        isExpanded={isGroupExpanded("institutions")}
                        onToggle={() => handleToggleGroup("institutions")}
                        onSelect={handleEntitySelect}
                      />
                    )}
                    {filteredFavorites.units.length > 0 && (
                      <CollapsibleResultGroup
                        title="Units"
                        icon={Network}
                        items={filteredFavorites.units}
                        isExpanded={isGroupExpanded("units")}
                        onToggle={() => handleToggleGroup("units")}
                        onSelect={handleEntitySelect}
                      />
                    )}
                    {filteredFavorites.researchers.length > 0 && (
                      <CollapsibleResultGroup
                        title="Researchers"
                        icon={User}
                        items={filteredFavorites.researchers}
                        isExpanded={isGroupExpanded("researchers")}
                        onToggle={() => handleToggleGroup("researchers")}
                        onSelect={handleEntitySelect}
                      />
                    )}
                  </div>
                ) : (
                  <div className="p-4 text-center text-sm text-gray-500 italic">
                    {isLoggedIn
                      ? "No favorite entities yet. Star entities to see your favorites here."
                      : "Type to search... Log in and star entities to see your favorites here."}
                  </div>
                )}
              </div>
            )}

            {/* Case 2: Input has text (length >= 1) AND has matching favorites */}
            {isFiltering && hasFilteredFavorites && (
              <div className="py-2 border-b border-gray-100">
                <div className="px-3.5 py-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-100 bg-gray-50/70">
                  <Star size={13} className="text-amber-500 fill-amber-500" />
                  <span>Favorite Matches</span>
                  <span className="text-xs text-gray-400 font-normal normal-case ml-auto">
                    matching "{trimmedSearch}"
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredFavorites.institutions.length > 0 && (
                    <CollapsibleResultGroup
                      title="Institutions"
                      icon={Building2}
                      items={filteredFavorites.institutions}
                      isExpanded={isGroupExpanded("institutions")}
                      onToggle={() => handleToggleGroup("institutions")}
                      onSelect={handleEntitySelect}
                    />
                  )}
                  {filteredFavorites.units.length > 0 && (
                    <CollapsibleResultGroup
                      title="Units"
                      icon={Network}
                      items={filteredFavorites.units}
                      isExpanded={isGroupExpanded("units")}
                      onToggle={() => handleToggleGroup("units")}
                      onSelect={handleEntitySelect}
                    />
                  )}
                  {filteredFavorites.researchers.length > 0 && (
                    <CollapsibleResultGroup
                      title="Researchers"
                      icon={User}
                      items={filteredFavorites.researchers}
                      isExpanded={isGroupExpanded("researchers")}
                      onToggle={() => handleToggleGroup("researchers")}
                      onSelect={handleEntitySelect}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Case 3: Exactly 1 character and no favorites match */}
            {trimmedSearch.length === 1 && !hasFilteredFavorites && (
              <div className="p-4 text-center text-sm text-gray-500">
                No favorites match "{trimmedSearch}". Keep typing to search...
              </div>
            )}

            {/* Case 4: Input has >= 2 characters -> Server Search Results */}
            {trimmedSearch.length >= 2 && (
              <div className="py-2">
                {isSearching && (
                  <div className="p-3 text-center text-xs text-gray-500 flex items-center justify-center gap-2">
                    <Loader2 className="text-gray-400 animate-spin" size={14} />
                    <span>Searching for more results...</span>
                  </div>
                )}

                {!isSearching && !hasDedupedServerResults && !hasFilteredFavorites && (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No results found for "{trimmedSearch}"
                  </div>
                )}

                {hasDedupedServerResults && (
                  <div>
                    {hasFilteredFavorites && (
                      <div className="px-3.5 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50">
                        More Results
                      </div>
                    )}
                    {dedupedServerResults.institutions.length > 0 && (
                      <ResultGroup
                        title="Institutions"
                        items={dedupedServerResults.institutions}
                        onSelect={handleEntitySelect}
                      />
                    )}
                    {dedupedServerResults.units.length > 0 && (
                      <ResultGroup
                        title="Units"
                        items={dedupedServerResults.units}
                        onSelect={handleEntitySelect}
                      />
                    )}
                    {dedupedServerResults.researchers.length > 0 && (
                      <ResultGroup
                        title="Researchers"
                        items={dedupedServerResults.researchers}
                        onSelect={handleEntitySelect}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="h-px bg-gray-100 my-1 w-full" />

      {/* Bottom Area: Recent Searches Chips */}
      <div className="min-h-[50px] flex items-center">
        <RecentSearchesChips recentEntities={recentEntities} onSelect={handleEntitySelect} />
      </div>
    </div>
  );
};

// Subcomponents for the Dropdown and Card

const CollapsibleResultGroup = ({
  title,
  icon: Icon,
  items,
  isExpanded,
  onToggle,
  onSelect,
}) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="w-full">
      <button
        type="button"
        aria-expanded={isExpanded}
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3.5 py-2 text-xs font-semibold text-gray-700 bg-gray-50/60 hover:bg-gray-100/70 transition-colors select-none text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={14} className="text-gray-500 flex-shrink-0" />
          <span className="uppercase tracking-wider text-[11px] font-semibold text-gray-700 truncate">
            {title}
          </span>
          <span className="text-[10px] font-medium text-gray-500 bg-white border border-gray-200 px-1.5 py-0.5 rounded-full flex-shrink-0 leading-none">
            {items.length}
          </span>
        </div>
        <ChevronDown
          size={14}
          className={cn(
            "text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2",
            isExpanded ? "transform rotate-180" : ""
          )}
        />
      </button>

      {isExpanded && (
        <div className="py-1">
          {items.map((entity, idx) => {
            let subtitle = "";
            const inst = entity.institution_name || entity.institution;
            if (inst) {
              subtitle = typeof inst === "object" ? (inst.name || "") : inst;
            }

            return (
              <div
                key={entity.id || idx}
                className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors group"
                onClick={() => onSelect(entity)}
              >
                <Icon size={15} className="text-gray-400 flex-shrink-0 group-hover:text-gray-600 transition-colors" />
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium text-gray-900 truncate">{entity.name}</span>
                  {subtitle && (
                    <span className="text-xs text-gray-500 truncate">{subtitle}</span>
                  )}
                </div>
                <Star size={13} className="text-amber-400 fill-amber-400 flex-shrink-0 opacity-80" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ResultGroup = ({ title, items, onSelect }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider bg-gray-50/50">
        {title}
      </div>
      {items.map((entity, idx) => {
        const Icon = entity.type === "institution" ? Building2 : entity.type === "unit" ? Network : User;

        let subtitle = "";
        const inst = entity.institution_name || entity.institution;
        if (inst) {
          subtitle = typeof inst === "object" ? (inst.name || "") : inst;
        }

        return (
          <div
            key={entity.id || idx}
            className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => onSelect(entity)}
          >
            <Icon size={16} className="text-gray-400 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-gray-900 truncate">{entity.name}</span>
              {subtitle && (
                <span className="text-xs text-gray-500 truncate">{subtitle}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecentSearchesChips = ({ recentEntities, onSelect }) => (
  <div className="flex items-center gap-3 w-full overflow-x-auto pb-1 scrollbar-hide">
    <div className="flex items-center gap-1.5 flex-shrink-0 mr-2">
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
        Recent Searches
      </span>
    </div>

    {recentEntities.length === 0 ? (
      <span className="text-xs text-gray-400 italic">No recent searches. Start by searching above.</span>
    ) : (
      <div className="flex items-center gap-2">
        {recentEntities.map((item) => {
          const Icon = item.type === "institution" ? Building2 : item.type === "unit" ? Network : User;
          const typeLabel = item.type === "institution" ? "Inst." : item.type === "unit" ? "Unit" : "Res.";

          return (
            <button
              key={item.id}
              onClick={() => onSelect(item)}
              className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-full text-xs text-gray-700 bg-white hover:border-gray-300 hover:bg-gray-50 transition-all flex-shrink-0 group"
            >
              <Icon size={12} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
              <span className="font-medium max-w-[120px] truncate">{item.name}</span>
              <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider">
                {typeLabel}
              </span>
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export default EntitySelector;
